import {
    Component,
    EventEmitter,
    Inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { Subject } from 'rxjs';
import { filter, takeUntil, tap } from 'rxjs/operators';
import { DisplayDecorator } from '../../decorators/display-decorator';
import { ODataModel, ODataModelChangeEvent } from '../../interfaces/odata-model.interface';
import { RadioModel } from '../../models/complex/radio.model';

@Component({
    selector: 'pace-core-radio-input',
    templateUrl: './radio-input.component.html',
    styleUrls: ['./radio-input.component.scss'],
    standalone: false,
})
export class RadioInputComponent implements OnInit, OnChanges, OnDestroy {
    @Input()
    public model: RadioModel<ODataModel>;

    @Input()
    public isReadonlyInputControl: boolean;

    // If true, for md breakpoint the radio options will display vertically (in diff rows) else in same row
    @Input()
    public useResponsiveLayout = false;

    @Output()
    public modelChange = new EventEmitter<RadioModel<ODataModel>>();

    @Input()
    public parentForm: FormArray | undefined;

    @Output()
    public valueChange = new EventEmitter<ODataModelChangeEvent<DisplayDecorator<ODataModel>>>();

    public radioGroup: FormGroup;
    public radioControl: AbstractControl;

    private destroySubject = new Subject<void>();
    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly formBuilder: FormBuilder,
    ) {}

    public ngOnInit(): void {
        const radioInputValidator: ValidatorFn = (): ValidationErrors | null => this.checkValidity();

        this.radioControl = this.formBuilder.control({
            value: this.model.selectedEntry,
            disabled: this.model.options.readonly,
        });

        this.radioGroup = this.formBuilder.group(
            {
                radioControl: this.radioControl,
            },
            {
                validators: [radioInputValidator],
            },
        );

        this.radioControl.valueChanges
            .pipe(
                filter(
                    (newValue: DisplayDecorator<ODataModel>) =>
                        !!newValue && this.model.selectedEntry?.value !== newValue?.value,
                ),
                tap((newVal: DisplayDecorator<ODataModel>) => {
                    const oldEntry = this.model.selectedEntry?.value;
                    this.model.selectedEntry = newVal;
                    this.modelChange.emit(this.model);
                    this.valueChange.emit({
                        oldValue: oldEntry,
                        newValue: this.model.selectedEntry.value,
                    });
                }),
                takeUntil(this.destroySubject),
            )
            .subscribe();

        this.model.newEntrySelected$.pipe(takeUntil(this.destroySubject)).subscribe(() => {
            this.radioGroup.updateValueAndValidity();
        });

        // The actual control needs to be added asynchronously to avoid an ExpressionChangedAfterItHasBeenCheckedError
        // See also https://stackoverflow.com/questions/45661010/dynamic-nested-reactive-form-expressionchangedafterithasbeencheckederror
        if (this.parentForm) {
            const parent = this.parentForm;
            Promise.resolve(undefined)
                .then(() => {
                    parent.push(this.radioGroup);
                })
                .catch((error: Error) =>
                    this.log.error(
                        'Error in RadioInputComponent -> ngOnInit -> parentForm.push: ',
                        JSON.stringify(error),
                    ),
                );
        }
    }

    public ngOnChanges(changesObj: SimpleChanges): void {
        if (changesObj && changesObj.model) {
            this.model = <RadioModel<ODataModel>>changesObj.model.currentValue;

            if (this.model.options.readonly && this.radioControl?.enabled) {
                // we shouldn't trigger radioControl.valueChanges just because we are disabling the radio-control
                this.radioControl.disable({ onlySelf: true, emitEvent: false });
            }
            if (!this.model.options.readonly && this.radioControl?.disabled) {
                // we shouldn't trigger radioControl.valueChanges just because we are enabling the radio-control
                this.radioControl.enable({ onlySelf: true, emitEvent: false });
            }

            this.radioControl?.setValue(this.model.selectedEntry);
        }
    }

    public ngOnDestroy(): void {
        if (this.parentForm) {
            const index = this.parentForm.controls.findIndex((control) => control === this.radioGroup);
            this.parentForm.removeAt(index);
        }
        this.destroySubject.next();
        this.destroySubject.complete();
    }

    private checkValidity(): ValidationErrors | null {
        if (this.model.getSelectedIndex() === -1) {
            return this.model.options.required ? { required: { valid: false, value: this.model.selectedEntry } } : null;
        }
        const selectionValid = this.model.getSelectedIndex() !== this.model.options.invalidSelection;
        return selectionValid ? null : { invalidSelection: { valid: false, value: this.model.selectedEntry } };
    }
}
